import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { normalizeSource } from "@/lib/authHelpers";
import { resolveScope, resolveStaffIds } from "@/lib/authScope";
import { decodeJwt } from "@/lib/jwt";
import { getMerchantClientId } from "@/lib/payerClient";
import type { Session } from "@/lib/session";
import { getLocalSession } from "@/hooks/useLocalSession";
import { setAuth } from "@/store/slices/authSlice";
import { safeStorage } from "@/shared/lib/safeStorage";
import type { Source } from "@/types";

interface JwtPayload {
  staffType?: string;
  staffTypeId?: number;
  source?: string;
  role?: string;
  isAgent?: boolean;
  merchantId?: string;
  targetDatabase?: string;
  clientId?: number;
  clinicId?: number;
}

/**
 * Populate the redux `auth` slice from the client session on mount / session
 * change. This is the preserved cross-cutting glue from the legacy app layout:
 * downstream pages read `state.auth.user`, so the shell keeps it in sync with
 * the JWT-backed session (which survives reloads). Session rendering itself
 * uses `useSession` directly — this only mirrors it into redux.
 */
export function useSyncReduxAuth(session: Session): void {
  const dispatch = useDispatch();

  const jwtPayload = useMemo<JwtPayload | null>(() => {
    const accessToken = session?.user?.accessToken;
    return accessToken ? (decodeJwt(accessToken) as JwtPayload | null) : null;
  }, [session]);

  useEffect(() => {
    if (!session) return;

    const local = getLocalSession();
    if (!local) return;

    const user = session.user;
    const p = jwtPayload;

    const role = user.role?.toLowerCase();
    const merchantId = p?.merchantId ?? p?.targetDatabase ?? user.merchantId;
    const username = user.username ?? user.name ?? undefined;
    // Dev-parity scope resolution: staffType map first (CLIENT → payer,
    // PROVIDER/ADMIN → clinic, …), PGLX platform source as the super-admin
    // fallback; session.scope (already resolved) is the last resort.
    const scope = (resolveScope({
      staffType: p?.staffType,
      source: p?.source,
      targetDatabase: p?.targetDatabase,
      role: p?.role ?? user.role,
    }) ?? normalizeSource(user.scope)) as Source | undefined;

    dispatch(
      setAuth({
        user: {
          username,
          role,
          merchantId,
          source: scope,
          isAgent: p?.isAgent === true || user.isAgent === true,
          ...resolveStaffIds({
            staffType: p?.staffType,
            staffTypeId: p?.staffTypeId,
            clientId: p?.clientId ?? user.clientId,
            clinicId: p?.clinicId ?? user.clinicId,
            employerId: user.employerId,
            groupId: user.groupId,
            divisionId: user.divisionId,
            locationId: user.locationId,
            memberId: user.memberId,
          }),
        },
        sessionId: local.sessionId,
      })
    );

    if (scope === "payer" && merchantId && getMerchantClientId(merchantId)) {
      safeStorage.setItem("payerMerchantId", merchantId);
    }
  }, [session, dispatch, jwtPayload]);
}
